import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, X, Send, ShieldCheck, Wand2, Pause, Play } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = "https://customer-assets-eiarnc6j.emergentagent.net/wingman/05a3193c-9053-43cf-b79c-afd2faf51841/attachments/11cf0c0f0191437ea9ac4ec66d77fe96_file_00000000451c8211bbc6b315f80c56df.png";
const BANNER = "https://customer-assets-eiarnc6j.emergentagent.net/wingman/05a3193c-9053-43cf-b79c-afd2faf51841/attachments/2daf1b4d5ef2421688efe4f53621e26c_file_000000003428820893b94ebb3d7797ea.png";

export default function NameCraft() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name_type: "baby", preferences: "", mobile: "", customer_name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/namecraft/settings`).then((r) => r.json()).then(setCfg).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.mobile.trim() || !form.preferences.trim()) { toast.error("Preferences & mobile required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/namecraft/order`, form);
      toast.success(`Order placed · ₹${r.data.price_inr}`);
      if (cfg?.features?.whatsapp_delivery && r.data.whatsapp_link) {
        window.open(r.data.whatsapp_link, "_blank");
      }
      setOpen(false);
      setForm({ name_type: "baby", preferences: "", mobile: "", customer_name: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); }
    finally { setBusy(false); }
  };

  const paused = cfg && !cfg.enabled;

  return (
    <div>
      <img src={HERO} alt="Unique Name Craft" className="w-full h-auto block" data-testid="namecraft-hero" />
      <img src={BANNER} alt="Craft My Unique Name" className="w-full h-auto block" data-testid="namecraft-banner" />

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 text-center">
        <div className="chip mb-4"><Wand2 className="w-3.5 h-3.5" /> Unique Name Craft Assist</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">Craft My Unique Name</h1>
        <p className="mt-3 text-neutral-600 max-w-xl mx-auto">
          Hand-crafted by our naming experts — baby, brand, business or product. Delivered on WhatsApp.
        </p>
        {cfg && (
          <div className="mt-6 flex flex-wrap gap-2 justify-center text-xs">
            <span className="chip"><ShieldCheck className="w-3.5 h-3.5" /> ₹{cfg.price_inr}</span>
            {cfg.features?.numerology && <span className="chip">Numerology match</span>}
            {cfg.features?.pdf_report && <span className="chip">PDF report</span>}
            {cfg.features?.whatsapp_delivery && <span className="chip">WhatsApp delivery</span>}
            {cfg.features?.personal_note && <span className="chip">Personal note</span>}
          </div>
        )}
        <div className="mt-8">
          {paused ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm px-5 py-3">
              <Pause className="w-4 h-4" /> Express feature is paused. Please check back soon.
            </div>
          ) : (
            <button data-testid="craft-cta" onClick={() => setOpen(true)}
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4">
              <Sparkles className="w-5 h-5" /> Craft My Unique Name
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="craft-modal">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-700"><X className="w-5 h-5" /></button>
            <h2 className="font-display text-2xl font-bold">Tell us about your name</h2>
            <p className="text-xs text-neutral-500 mt-1">₹{cfg?.price_inr || "—"} · delivery via WhatsApp</p>
            <div className="mt-5 space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Name type</Label>
                <select data-testid="nc-type" value={form.name_type} onChange={(e) => setForm({ ...form, name_type: e.target.value })}
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="baby">Baby</option>
                  <option value="brand">Brand</option>
                  <option value="business">Business</option>
                  <option value="product">Product</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Preferences / hints</Label>
                <Textarea data-testid="nc-prefs" rows={3} placeholder="e.g. modern, 2 syllables, spiritual meaning, initial 'A'"
                  value={form.preferences} onChange={(e) => setForm({ ...form, preferences: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Your name</Label>
                  <Input data-testid="nc-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Mobile</Label>
                  <Input data-testid="nc-mobile" type="tel" placeholder="+91…" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                </div>
              </div>
              <button data-testid="nc-submit" onClick={submit} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-1.5">
                <Send className="w-4 h-4" /> {busy ? "Placing…" : `Pay ₹${cfg?.price_inr || ""} & confirm`}
              </button>
              <p className="text-[11px] text-neutral-500 text-center">
                Order confirmation lands on your WhatsApp. Our expert crafts your name and delivers within 24 hours.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Admin NameCraft panel ----------
export function AdminNameCraft({ token }) {
  const hdr = { Authorization: `Bearer ${token}` };
  const [cfg, setCfg] = useState(null);
  const [orders, setOrders] = useState([]);
  const [deliverInputs, setDeliverInputs] = useState({});
  const refresh = () => {
    axios.get(`${API}/admin/namecraft/settings`, { headers: hdr }).then((r) => setCfg(r.data.settings));
    axios.get(`${API}/admin/namecraft/orders`, { headers: hdr }).then((r) => setOrders(r.data.orders || []));
  };
  useEffect(() => { refresh(); }, [token]); // eslint-disable-line
  if (!cfg) return <div className="p-4 text-neutral-500 text-sm">Loading…</div>;
  const save = (patch) => axios.post(`${API}/admin/namecraft/settings`, patch, { headers: hdr })
    .then((r) => { setCfg(r.data.settings); toast.success("Saved"); });
  const deliver = (o) => {
    const v = deliverInputs[o.id] || { name: "", note: "" };
    if (!v.name.trim()) { toast.error("Enter the crafted name"); return; }
    axios.post(`${API}/admin/namecraft/orders/${o.id}/deliver`, v, { headers: hdr })
      .then(() => { toast.success("Delivered"); refresh(); });
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-3xl border border-black/10 bg-white p-6">
        <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>NameCraft settings</div>
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <button data-testid="nc-toggle" onClick={() => save({ enabled: !cfg.enabled })}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${cfg.enabled ? "bg-emerald-500 text-white" : "border-2 border-amber-300 bg-amber-50 text-amber-800"}`}>
              {cfg.enabled ? <><Play className="w-4 h-4" /> Active</> : <><Pause className="w-4 h-4" /> Paused</>}
            </button>
            <div className="text-xs text-neutral-500">Toggle the express feature live</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Price (₹)</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="nc-price" type="number" value={cfg.price_inr} onChange={(e) => setCfg({ ...cfg, price_inr: Number(e.target.value) })} />
              <button onClick={() => save({ price_inr: cfg.price_inr })} className="btn-primary text-sm px-4">Save</button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Features</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {Object.entries(cfg.features || {}).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`nc-f-${k}`} checked={v}
                    onChange={(e) => save({ features: { ...cfg.features, [k]: e.target.checked } })} />
                  {k.replace("_", " ")}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-3xl border border-black/10 bg-white p-6">
        <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Orders ({orders.length})</div>
        <div className="mt-4 space-y-3 max-h-[520px] overflow-y-auto">
          {orders.map((o) => {
            const inp = deliverInputs[o.id] || { name: "", note: "" };
            return (
              <div key={o.id} className="rounded-xl border border-black/10 p-3 text-sm">
                <div className="flex justify-between"><b className="capitalize">{o.name_type}</b>
                  <span className={`chip ${o.status === "delivered" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : ""}`}>{o.status}</span>
                </div>
                <div className="text-xs text-neutral-500 mt-1">{o.customer_name || "—"} · {o.mobile} · ₹{o.price_inr}</div>
                <div className="text-xs mt-1"><b>Prefs:</b> {o.preferences}</div>
                {o.status === "delivered" ? (
                  <div className="mt-2 text-xs text-emerald-700"><b>Delivered:</b> {o.delivered_name} — {o.delivered_note}</div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <Input placeholder="Crafted name" value={inp.name} onChange={(e) => setDeliverInputs({ ...deliverInputs, [o.id]: { ...inp, name: e.target.value } })} />
                    <Input placeholder="Personal note (optional)" value={inp.note} onChange={(e) => setDeliverInputs({ ...deliverInputs, [o.id]: { ...inp, note: e.target.value } })} />
                    <button onClick={() => deliver(o)} className="btn-primary text-xs px-3 py-1.5">Deliver</button>
                  </div>
                )}
              </div>
            );
          })}
          {orders.length === 0 && <div className="text-xs text-neutral-500">No orders yet.</div>}
        </div>
      </div>
    </div>
  );
}
